import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Star, Trash2, Plus } from 'lucide-react';
import type { OpportunityView } from '../types';

interface ViewsDropdownProps {
    views: OpportunityView[];
    onApply: (view: OpportunityView) => void;
    onSave: (name: string) => void;
    onToggleFavorite: (view: OpportunityView) => void;
    onDelete: (id: string) => void;
    isSaving?: boolean;
}

export function ViewsDropdown({ views, onApply, onSave, onToggleFavorite, onDelete, isSaving }: ViewsDropdownProps) {
    const [open, setOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const favorite = views.find((v) => v.isFavorite);

    const handleSave = () => {
        const name = newName.trim();
        if (!name) return;
        onSave(name);
        setNewName('');
    };

    return (
        <div ref={ref} className="relative">
            <button type="button" onClick={() => setOpen((o) => !o)} className="btn-secondary">
                <Star className="w-4 h-4" />
                {favorite ? favorite.name : 'Vues'}
                <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute z-10 mt-1 right-0 w-80 card p-2 shadow-lg">
                    <div className="space-y-0.5 max-h-56 overflow-y-auto">
                        {views.length === 0 && (
                            <p className="text-sm text-[var(--color-text-muted)] px-2 py-1.5">
                                Aucune vue enregistrée pour l'instant
                            </p>
                        )}
                        {views.map((v) => (
                            <div key={v.id} className="flex items-center gap-1 rounded-lg hover:bg-[var(--color-bg-hover)]">
                                <button
                                    type="button"
                                    onClick={() => onToggleFavorite(v)}
                                    className="p-1.5 flex-shrink-0"
                                    title={v.isFavorite ? 'Retirer des vues par défaut' : 'Définir comme vue par défaut'}
                                >
                                    <Star
                                        className="w-4 h-4"
                                        style={v.isFavorite
                                            ? { fill: 'var(--color-warning)', color: 'var(--color-warning)' }
                                            : { color: 'var(--color-text-muted)' }}
                                    />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { onApply(v); setOpen(false); }}
                                    className="flex-1 min-w-0 text-left text-sm text-[var(--color-text)] py-1.5 truncate"
                                >
                                    {v.name}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onDelete(v.id)}
                                    className="p-1.5 flex-shrink-0"
                                    title="Supprimer cette vue"
                                >
                                    <Trash2 className="w-4 h-4 text-[var(--color-text-muted)] hover:text-[var(--color-error)]" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-[var(--color-border)] mt-2 pt-2 flex gap-2">
                        <input
                            className="input"
                            placeholder="Nom de la vue"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); } }}
                        />
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!newName.trim() || isSaving}
                            className="btn-primary flex-shrink-0"
                            title="Enregistrer les filtres actuels comme nouvelle vue"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
